import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react'
import { getFileFromUser } from '@weblite/file'
import { Button, IconButton, Typography, useTheme } from '@mui/material'
import { AttachmentIcon, WAppIcon } from '@weblite/icons'
import { RoomTypes } from '@weblite/ts-action/room'
import { SendIcon } from '$common/icons/send'
import { EditTickIcon } from '$common/icons/edit-tick-icon'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { getOpenedRoomId, useOpenedRoomPermissions } from '$logic/opened-room'
import { sendNewMessage } from '$logic/message'
import { SEND_FIELD_BORDER_TOP_COLOR } from '$comp/_app/components/chat/components/conf'
import { RecordButton } from '../record-button'
import { TextEditor } from '../text-editor'
import { SendMessageButton } from '../send-message-button'
import { RecorderStatus, SendFieldAction } from '../../send-field.type'
import { showChatToolBoxModal } from '$comp/_app/components/chat/components/footer/components/send-field/components/chat-toolbox-modal/chat-toolbox-modal.api'
import {
  changeFileTypeToValidFileOrAudioMessageType,
  checkIfFileIsAMedia,
} from '../../helper'
import { MentionMenu } from '../mention-menu'
import { t } from '$i18n'
import { validateMessageFileSizeAndShowError } from '$logic/message/helper'
import { useOnboarding } from '$logic/onboarding/react-hooks'
import { CommonOnboarding } from '$logic/onboarding/types'
import { HotSpot } from '$common/tour/components/hot-spot'

function WappButton({ onClick }: { onClick: () => unknown }) {
  const theme = useTheme()
  const ref = useRef<HTMLButtonElement>(null)

  const { enabled: onboardingEnabled, completeOnboarding } = useOnboarding(
    CommonOnboarding.CHAT_WAPP_BUTTON,
  )

  const handleClick = useCallback(() => {
    onClick?.()
    completeOnboarding()
  }, [completeOnboarding, onClick])

  return (
    <Button
      onClick={handleClick}
      ref={ref}
      startIcon={
        <WAppIcon
          colorOne={theme.palette.primary500}
          colorTwo={theme.palette.error500}
          colorThree={theme.palette.yellow500}
          colorFour={theme.palette.success500}
        />
      }
      sx={{
        backgroundColor: 'neutral100',
        padding: '0 8px',
        height: '40px',
        minWidth: '75px',
        position: 'relative',
      }}
    >
      {onboardingEnabled && (
        <HotSpot
          sx={{ position: 'absolute', top: '-4px', right: '-4px' }}
          containerRef={ref}
        />
      )}
      <Typography color="neutral700" variant="subtitle3">
        {t('chatList.tabs.wappRoom')}
      </Typography>
    </Button>
  )
}

interface Props {
  action: SendFieldAction
  mentionIsDisabled: boolean
  recorderState: RecorderStatus
  submitSendField: () => void
  setRecorderState: (state: RecorderStatus) => unknown
  setSelectedFile: (file: File) => unknown
  setMentionQuery: Dispatch<SetStateAction<string | undefined>>
  hasFile: boolean
  imageCaptionMode: boolean
  mentionQuery?: string
  onEditorTextChange: (value: string) => unknown
  bottomOffset: number
  isHidden: boolean
  roomType: RoomTypes
}

export function TextPreview({
  action,
  mentionIsDisabled,
  recorderState,
  setRecorderState,
  submitSendField,
  onEditorTextChange,
  setSelectedFile,
  setMentionQuery,
  hasFile,
  imageCaptionMode,
  mentionQuery,
  bottomOffset,
  isHidden,
  roomType,
}: Props) {
  const isDesktopSize = useIsDesktopSize()
  const [hasMessage, setHasMessage] = useState(false)

  const { openedRoomPermissions } = useOpenedRoomPermissions()
  const canSendMedia = openedRoomPermissions?.sendMedia

  const textMode =
    hasMessage || hasFile || action.type === 'EDIT' || action.type === 'FORWARD'

  const onAttachmentClick = useCallback(() => {
    getFileFromUser(
      '*',
      (files: File | File[]) => {
        const roomId = getOpenedRoomId()
        if (Array.isArray(files) && files.length > 1) {
          files.forEach(file => {
            if (!validateMessageFileSizeAndShowError(file)) return
            const isMedia = checkIfFileIsAMedia(file)
            if (isMedia)
              sendNewMessage(file, undefined, undefined, roomId, roomType)
            else
              sendNewMessage(
                changeFileTypeToValidFileOrAudioMessageType(file),
                undefined,
                undefined,
                roomId,
                roomType,
              )
          })
        } else {
          const file = Array.isArray(files) ? files[0] : files
          if (!validateMessageFileSizeAndShowError(file)) return
          const isMedia = checkIfFileIsAMedia(file)
          setSelectedFile(
            isMedia ? file : changeFileTypeToValidFileOrAudioMessageType(file),
          )
        }
      },
      true,
    )
  }, [roomType, setSelectedFile])

  return (
    <div
      style={{
        zIndex: 1,
        display: isHidden ? 'none' : 'flex',
        justifyContent: 'space-between',
        alignItems: 'end',
        position: imageCaptionMode ? 'absolute' : 'relative',
        bottom: imageCaptionMode ? bottomOffset : 'unset',
        width: imageCaptionMode ? '100%' : 'unset',
        boxSizing: 'border-box',
        backgroundColor: imageCaptionMode ? 'unset' : 'white',

        ...(isDesktopSize &&
          imageCaptionMode && {
            position: 'fixed',
            left: 0,
            right: 0,
            zIndex: 2,
          }),
      }}
      onPaste={e => {
        if (e.clipboardData.files.length > 0)
          setSelectedFile(e.clipboardData.files[0])
      }}
    >
      <div
        style={{
          position: 'absolute',
          height: '100%',
          width: '100%',
          backgroundColor: imageCaptionMode ? 'black' : 'unset',
          opacity: '0.5',
          zIndex: -1,
        }}
      />

      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {imageCaptionMode && mentionQuery !== undefined && (
          <MentionMenu imageCaptionMode query={mentionQuery} />
        )}

        <div
          style={{
            display: 'flex',
            width: '100%',
            padding: '7px 8px',
            backgroundColor: imageCaptionMode ? '#00000080' : 'unset',
            borderTop: imageCaptionMode
              ? 'unset'
              : `1px solid ${SEND_FIELD_BORDER_TOP_COLOR}`,
            justifyContent: 'space-between',
            alignItems: 'center',
            boxSizing: 'border-box',
            bottom: imageCaptionMode ? 0 : 'unset',
          }}
        >
          {textMode && (
            <SendMessageButton
              onSend={submitSendField}
              disabled={!hasMessage && !hasFile && action.type === 'EDIT'}
            >
              {action.type === 'EDIT' ? <EditTickIcon /> : <SendIcon />}
            </SendMessageButton>
          )}

          {((!textMode && canSendMedia) || recorderState === 'PREVIEW') && (
            <RecordButton
              state={recorderState}
              setRecorderState={setRecorderState}
            />
          )}

          {!textMode && canSendMedia && (
            <IconButton onClick={onAttachmentClick}>
              <AttachmentIcon sx={{ color: 'neutral500' }} />
            </IconButton>
          )}

          <TextEditor
            action={action}
            mentionIsDisabled={mentionIsDisabled}
            darkMode={imageCaptionMode}
            placeholder={
              imageCaptionMode
                ? t('chat.sendFieldCaption')
                : t('chat.sendField')
            }
            setHasMessage={setHasMessage}
            onContentChange={onEditorTextChange}
            setMentionQuery={setMentionQuery}
            onSubmit={submitSendField}
            onPasteFile={setSelectedFile}
          />

          {((!textMode && canSendMedia) || recorderState === 'PREVIEW') && (
            <WappButton onClick={() => showChatToolBoxModal()} />
          )}
        </div>
      </div>
    </div>
  )
}
